import type { ApolloClient } from '@apollo/client';
import type { ChatAttachment, ChatAttachmentUpload } from '@newfront-insurance/ai-api';
import { graphql } from '@newfront-insurance/graphql-frontend';

import { normalizeChatUuid } from '../components/chat-v2/lib/notification-helpers';

/**
 * The chat's sandbox workspace: attachments upload straight into it (presigned multipart S3 PUTs
 * that api-ai signs and commits when the message is sent), and files in it download through
 * short-lived signed links.
 */
const CREATE_NOVA_CHAT_ATTACHMENT_UPLOAD = graphql(`
  mutation CreateNovaChatAttachmentUpload($chatUuid: String!, $input: CreateNovaChatAttachmentUploadInput!) {
    createNovaChatAttachmentUpload(chatUuid: $chatUuid, input: $input) {
      path
      name
      uploadId
      partSize
      parts {
        partNumber
        url
      }
    }
  }
`);

const GET_NOVA_CHAT_ATTACHMENT_DOWNLOAD_URL = graphql(`
  query GetNovaChatAttachmentDownloadUrl($chatUuid: String!, $path: String!) {
    getNovaChatAttachmentDownloadUrl(chatUuid: $chatUuid, path: $path)
  }
`);

const PART_CONCURRENCY = 4;
const PART_ATTEMPTS = 3;

/** Uploads one file into the chat's `inputs/`; the message that carries the result commits it. */
export async function uploadChatAttachment(
  apollo: Pick<ApolloClient<object>, 'mutate'>,
  { chatUuid, file, signal }: { chatUuid: string; file: File; signal?: AbortSignal },
): Promise<ChatAttachmentUpload> {
  const contentType = file.type || 'application/octet-stream';
  const { data } = await apollo.mutate({
    mutation: CREATE_NOVA_CHAT_ATTACHMENT_UPLOAD,
    variables: {
      chatUuid: normalizeChatUuid(chatUuid),
      input: { filename: file.name, size: file.size, contentType },
    },
    context: { fetchOptions: { signal } },
  });
  const upload = data?.createNovaChatAttachmentUpload;
  if (!upload) {
    throw new Error('Nova could not start the upload');
  }

  const queue = [...upload.parts];
  const worker = async (): Promise<void> => {
    const part = queue.shift();
    if (!part) {
      return;
    }
    const start = (part.partNumber - 1) * upload.partSize;
    await putPart(part.url, file.slice(start, start + upload.partSize), signal);
    await worker();
  };
  await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, queue.length) }, worker));

  return { path: upload.path, uploadId: upload.uploadId, name: upload.name, size: file.size, contentType };
}

/** A short-lived link to a file of the chat's workspace (its inputs, or a schedule's). */
export async function chatWorkspaceFileUrl(
  apollo: Pick<ApolloClient<object>, 'query'>,
  { chatUuid, path }: { chatUuid: string; path: string },
): Promise<string> {
  const { data } = await apollo.query({
    query: GET_NOVA_CHAT_ATTACHMENT_DOWNLOAD_URL,
    variables: { chatUuid: normalizeChatUuid(chatUuid), path },
    // Signed links expire; never serve one from the cache.
    fetchPolicy: 'no-cache',
  });
  return data.getNovaChatAttachmentDownloadUrl;
}

/** The record a sent message keeps of an upload, for rendering it before the server's copy arrives. */
export function uploadRecord(upload: ChatAttachmentUpload): ChatAttachment {
  return { name: upload.name, path: upload.path, size: upload.size, contentType: upload.contentType, source: 'upload' };
}

/** One part, retried: the URL is presigned, so the body goes as-is, with no extra headers. */
async function putPart(url: string, body: Blob, signal?: AbortSignal, attempt = 1): Promise<void> {
  try {
    const response = await fetch(url, { method: 'PUT', body, signal });
    if (!response.ok) {
      throw new Error(`Upload failed (${response.status})`);
    }
  } catch (error) {
    if (signal?.aborted || attempt >= PART_ATTEMPTS) {
      throw error;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, 500 * 2 ** attempt);
    });
    await putPart(url, body, signal, attempt + 1);
  }
}

/** The uploads of these attachments that finished and can go with the message. */
export function readyUploads(attachments: ReadonlyArray<{ upload?: ChatAttachmentUpload; uploadError?: string }>) {
  return attachments.flatMap(({ upload, uploadError }) => (upload && !uploadError ? [upload] : []));
}

/** The uploads the composer put in the request context for the next message. */
export function contextUploads(additionalContext: Record<string, unknown> | undefined): ChatAttachmentUpload[] {
  const uploads = additionalContext?.attachmentUploads;
  return Array.isArray(uploads)
    ? uploads.filter((upload): upload is ChatAttachmentUpload => typeof upload?.path === 'string')
    : [];
}

/** Whether the composer's finished uploads and the request context disagree (a send would drop some). */
export function hasUnsyncedAttachments(
  attachments: ReadonlyArray<{ upload?: ChatAttachmentUpload; uploadError?: string }>,
  additionalContext: Record<string, unknown> | undefined,
): boolean {
  const ready = readyUploads(attachments).map(({ path }) => path);
  const inContext = new Set(contextUploads(additionalContext).map(({ path }) => path));
  return ready.length !== inContext.size || ready.some((path) => !inContext.has(path));
}
